/**
 * Avatar Component - User avatar with fallback
 *
 * Variants:
 * - default: Circular avatar
 * - square: Square avatar
 *
 * Sizes:
 * - xs: Extra small (24px)
 * - sm: Small (32px)
 * - md: Medium (40px)
 * - lg: Large (48px)
 * - xl: Extra large (64px)
 * - 2xl: 2X Large (96px)
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, getInitials, getAvatarColor } from '@/shared/lib/utils';
import { User } from 'lucide-react';

const avatarVariants = cva(
  'inline-flex items-center justify-center overflow-hidden font-medium text-white',
  {
    variants: {
      variant: {
        default: 'rounded-full',
        square: 'rounded-md',
      },
      size: {
        xs: 'h-6 w-6 text-xs',
        sm: 'h-8 w-8 text-xs',
        md: 'h-10 w-10 text-sm',
        lg: 'h-12 w-12 text-base',
        xl: 'h-16 w-16 text-lg',
        '2xl': 'h-24 w-24 text-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface AvatarProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {
  src?: string;
  alt?: string;
  name?: string;
  fallback?: string;
}

/**
 * Avatar Component
 *
 * @example
 * ```tsx
 * <Avatar src="/avatar.png" alt="User" />
 * <Avatar name="John Doe" />
 * <Avatar name="Jane" fallback="J" size="lg" />
 * <Avatar variant="square" />
 * ```
 */
const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, variant, size, src, alt, name, fallback, ...props }, ref) => {
    const [imageError, setImageError] = React.useState(false);

    const content = (() => {
      // Show image if available and no error
      if (src && !imageError) {
        return (
          <img
            src={src}
            alt={alt || name || 'Avatar'}
            onError={() => setImageError(true)}
            className="h-full w-full object-cover"
          />
        );
      }

      // Show fallback or initials
      if (fallback) {
        return <span>{fallback}</span>;
      }

      if (name) {
        return (
          <span className="font-medium">
            {getInitials(name, variant === 'square' ? 2 : 1)}
          </span>
        );
      }

      // Default icon
      return <User className="h-1/2 w-1/2" />;
    })();

    const backgroundColor = name ? getAvatarColor(name) : 'bg-neutral-400';

    return (
      <div
        ref={ref}
        className={cn(avatarVariants({ variant, size }), backgroundColor, className)}
        {...props}
      >
        {content}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

export { Avatar, avatarVariants };
export default Avatar;
